// Helper function for parsing date strings in the format "15 Dec 2025, 09:00"
export const parseEventDate = (dateStr: string) => {
  const [datePart, timePart] = dateStr.split(', ')
  const [day, month, year] = datePart.split(' ')
  const monthMap: { [key: string]: string } = {
    Jan: '01',
    Feb: '02',
    Mar: '03',
    Apr: '04',
    May: '05',
    Jun: '06',
    Jul: '07',
    Aug: '08',
    Sep: '09',
    Oct: '10',
    Nov: '11',
    Dec: '12',
  }
  const monthNum = monthMap[month]
  return new Date(`${year}-${monthNum}-${day.padStart(2, '0')}T${timePart}:00`)
}

export const getCurrentTime = (): string => {
  const now = new Date()
  const hours = now.getHours().toString().padStart(2, '0')
  const minutes = now.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

export const formatTime = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

export const createDateTimeFromParts = (
  date: Date,
  timeString: string
): Date => {
  const [hours, minutes] = timeString.split(':').map(Number)
  const newDate = new Date(date)
  newDate.setHours(hours, minutes, 0, 0)
  return newDate
}

export const formatDisplayDateTime = (
  date: Date | string | undefined
): string => {
  if (!date) return ''

  const newDate = new Date(date)

  return newDate.toLocaleString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export const isSameDate = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

export const getDateWithoutTime = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export const formatDateDisplay = (date: Date | string | undefined): string => {
  if (!date) return ''

  const newDate = new Date(date)

  return newDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export const convertTo12Hour = (
  hour24: number
): { hour12: number; period: 'AM' | 'PM' } => {
  if (hour24 === 0) return { hour12: 12, period: 'AM' }
  if (hour24 < 12) return { hour12: hour24, period: 'AM' }
  if (hour24 === 12) return { hour12: 12, period: 'PM' }
  return { hour12: hour24 - 12, period: 'PM' }
}

export const convertTo24Hour = (
  hour12: number,
  period: 'AM' | 'PM'
): number => {
  if (period === 'AM') {
    return hour12 === 12 ? 0 : hour12
  } else {
    return hour12 === 12 ? 12 : hour12 + 12
  }
}

export const formatTime12Hour = (
  date: Date
): { hour: string; minute: string; period: 'AM' | 'PM' } => {
  const hour24 = date.getHours()
  const minute = date.getMinutes()
  const { hour12, period } = convertTo12Hour(hour24)

  return {
    hour: String(hour12).padStart(2, '0'),
    minute: String(minute).padStart(2, '0'),
    period,
  }
}

export const TIME_PERIOD = {
  AM: 'AM' as const,
  PM: 'PM' as const,
}

export const allAvailableHours12 = [
  '12',
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
  '07',
  '08',
  '09',
  '10',
  '11',
]

export const TWELVE_STRING = '12'

export const normalizeDate = (date: Date | string) => {
  const newDate = new Date(date)
  newDate.setHours(0, 0, 0, 0)
  return newDate
}

export const combineDateTime = (date?: Date, time?: Date): Date | undefined => {
  if (!date) return undefined
  const combined = new Date(date)
  if (time) {
    combined.setHours(time.getHours())
    combined.setMinutes(time.getMinutes())
    combined.setSeconds(0)
    combined.setMilliseconds(0)
  }
  return combined
}

export const formatTime12HourString = (date: Date | undefined): string => {
  if (!date) return ''
  const { hour, minute, period } = formatTime12Hour(date)
  return `${hour}:${minute} ${period}`
}

export const extractTimeFromDateTime = (
  datetime: Date | string | undefined
): Date | undefined => {
  if (!datetime) return undefined
  const date = new Date(datetime)
  const timeOnlyDate = new Date()
  timeOnlyDate.setHours(date.getHours())
  timeOnlyDate.setMinutes(date.getMinutes())
  timeOnlyDate.setSeconds(0)
  timeOnlyDate.setMilliseconds(0)
  return timeOnlyDate
}
